/*
  Projects index — drag + eased edge-hover horizontal scroll

  Pointer devices (fine pointer): click-and-drag to pan the card row, AND
  hovering the left/right edge zones auto-scrolls. The scroll speed is EASED
  toward a target (not set instantly), so it accelerates and decelerates
  smoothly and glides to rest when the cursor leaves the zone — rather than
  snapping between full speed and a dead stop.

  Touch / coarse pointer: no JS — native overflow-x swipe handles it.

  A drag past a small threshold suppresses the click that follows, so panning
  across the row never accidentally opens a project.
*/

(function () {
  // Edge auto-scroll zone as a fraction of the viewport width.
  var HOVER_ZONE = 0.2;
  // Top speed (px/frame, ~60fps) reached at the very edge.
  var MAX_SPEED = 16;
  // Velocity easing per frame toward the target — smaller = smoother / lazier.
  var ACCEL = 0.07;
  // Curve applied to edge intensity — >1 softens the onset near the inner
  // boundary so the ramp-up feels gentle, then builds toward the edge.
  var CURVE = 1.7;
  // Below this speed with no target, the glide is considered settled.
  var SETTLE = 0.05;
  // Movement (px) before a press counts as a drag rather than a click.
  var DRAG_THRESHOLD = 6;

  function ProjectIndex(root) {
    this.root = root; // the scroll container (.project-index)
    this.velocity = 0; // current eased speed
    this.target = 0; // desired speed from the cursor position
    this.pos = 0; // sub-pixel scroll accumulator
    this.raf = null;
    this.isDown = false;
    this.dragged = false;
    this.startX = 0;
    this.startScroll = 0;
    this.prevX = 0; // last pointer x during a drag (for release momentum)
    this.dragVel = 0; // scroll px/frame at the moment of release

    // Per-instance overrides (data-* attrs) — the gallery uses a subtler edge
    // zone and release momentum; the index keeps the defaults.
    this.hoverZone = parseFloat(root.dataset.hoverZone) || HOVER_ZONE;
    this.maxSpeed = parseFloat(root.dataset.maxSpeed) || MAX_SPEED;
    this.momentum = root.dataset.momentum === 'true';

    this.bindDrag();
    this.bindEdgeScroll();

    // Stop the browser's native image-drag (ghost copy) hijacking the pan.
    this.root.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });
  }

  ProjectIndex.prototype.bindDrag = function () {
    var self = this;

    this.root.addEventListener('pointerdown', function (e) {
      if (e.button && e.button !== 0) return; // primary button only
      self.isDown = true;
      self.dragged = false;
      self.startX = e.clientX;
      self.prevX = e.clientX;
      self.dragVel = 0;
      self.startScroll = self.root.scrollLeft;
      self.target = 0; // dragging overrides edge-scroll
      self.velocity = 0;
      self.root.classList.add('is-grabbing');
    });

    window.addEventListener('pointermove', function (e) {
      if (!self.isDown) return;
      var dx = e.clientX - self.startX;
      if (Math.abs(dx) > DRAG_THRESHOLD) self.dragged = true;
      self.root.scrollLeft = self.startScroll - dx;
      // Track instantaneous scroll speed for release momentum (scroll moves
      // opposite the pointer, hence the negation).
      self.dragVel = -(e.clientX - self.prevX);
      self.prevX = e.clientX;
    });

    var release = function () {
      if (!self.isDown) return;
      self.isDown = false;
      self.root.classList.remove('is-grabbing');
      // Fling: seed the eased loop with the release speed and let it decay
      // toward 0 (target = 0) — that decay IS the momentum glide.
      if (self.momentum && Math.abs(self.dragVel) > 1) {
        self.velocity = self.dragVel;
        self.target = 0;
        self.kick();
      }
    };
    window.addEventListener('pointerup', release);
    window.addEventListener('pointercancel', release);

    // Suppress the click that immediately follows a drag (capture phase, so it
    // beats the card link's own handler) — without this, panning opens a page.
    this.root.addEventListener(
      'click',
      function (e) {
        if (self.dragged) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );
  };

  ProjectIndex.prototype.bindEdgeScroll = function () {
    var self = this;

    this.root.addEventListener('mousemove', function (e) {
      if (self.isDown) {
        self.target = 0;
        return;
      }
      var rect = self.root.getBoundingClientRect();
      var relX = (e.clientX - rect.left) / rect.width; // 0..1 across the viewport

      var zone = self.hoverZone;
      if (relX < zone) {
        var iLeft = (zone - relX) / zone; // 0 at inner boundary → 1 at edge
        self.target = -Math.pow(iLeft, CURVE) * self.maxSpeed;
      } else if (relX > 1 - zone) {
        var iRight = (relX - (1 - zone)) / zone;
        self.target = Math.pow(iRight, CURVE) * self.maxSpeed;
      } else {
        self.target = 0;
      }

      self.kick();
    });

    // Glide to rest rather than hard-stopping when the cursor leaves.
    this.root.addEventListener('mouseleave', function () {
      self.target = 0;
      self.kick();
    });
  };

  ProjectIndex.prototype.kick = function () {
    if (this.raf) return;
    var self = this;
    self.pos = self.root.scrollLeft; // re-sync before a fresh glide

    var tick = function () {
      // Ease the current speed toward the target.
      self.velocity += (self.target - self.velocity) * ACCEL;

      // Settled: no target and effectively stopped.
      if (self.target === 0 && Math.abs(self.velocity) < SETTLE) {
        self.velocity = 0;
        self.raf = null;
        return;
      }

      var max = self.root.scrollWidth - self.root.clientWidth;
      self.pos += self.velocity;

      // Clamp + park at the boundaries; stop the loop if jammed into one.
      if (self.pos <= 0) {
        self.pos = 0;
        self.velocity = 0;
        self.root.scrollLeft = 0;
        if (self.target <= 0) {
          self.raf = null;
          return;
        }
      } else if (self.pos >= max) {
        self.pos = max;
        self.velocity = 0;
        self.root.scrollLeft = max;
        if (self.target >= 0) {
          self.raf = null;
          return;
        }
      } else {
        self.root.scrollLeft = self.pos;
      }

      self.raf = window.requestAnimationFrame(tick);
    };

    this.raf = window.requestAnimationFrame(tick);
  };

  function init() {
    // Pointer devices only — touch uses native overflow-x swipe.
    if (!window.matchMedia || !window.matchMedia('(pointer: fine)').matches) return;
    document.querySelectorAll('[data-project-index]').forEach(function (el) {
      if (el._projectIndexInit) return;
      el._projectIndexInit = true;
      new ProjectIndex(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
